import { Value } from "@bufbuild/protobuf";
import { type ExternalContext } from "@reboot-dev/reboot";
import { type WebContext } from "@reboot-dev/reboot-web";
import { List, Map } from "immutable";

import {
	type ActionIntent,
	type QueryEvaluatorState,
	type QueryPersistence,
	contentId,
	dehydrateTableMutation,
} from "@moment/api-collab/differential";
import { RunnableQuery } from "@moment/api-collab/differential/RunnableQuery";
import { Tuple } from "@moment/api-collab/differential/Tuple";
import { ZSet } from "@moment/api-collab/differential/ZSet";
import { type V1BaseSpec as BaseSpec } from "@moment/api-collab/differential/v1-base-spec";

import { type Store as ServerStore } from "../../dist/dev/moment/differential/v1alpha1/store_rbt";
import { type Store as WebStore } from "../../dist/dev/moment/differential/v1alpha1/store_rbt_web";

type ContextFor<S> = S extends WebStore.WeakReference
	? WebContext
	: S extends ServerStore.WeakReference
		? ExternalContext
		: never;

export const queryEvaluatorStateFromReboot = async <
	S extends WebStore.WeakReference | ServerStore.WeakReference,
>(
	context: ContextFor<S>,
	rebootStore: S,
	baseSpec: BaseSpec,
	signal: AbortSignal
): Promise<QueryEvaluatorState> => {
	// Create initial tables from base spec
	let tables = Map<string, ZSet<unknown>>();
	let currentEpoch = 0;
	for (const [tableName] of Object.entries(baseSpec.tables)) {
		const tableId = tableName;
		// This stupid-looking cast is because I could not figure out how to get TypeScript to not
		// complain even though we've "proved" in the signature that `context` is of the correct
		// type.
		const tableState = await rebootStore.tableState(context as any, { tableId }, { signal });
		// FIXME: not at all right.
		currentEpoch = Math.max(currentEpoch, tableState.currentEpoch);
		tables = tables.set(
			tableName,
			ZSet.from<unknown>(
				tableState.tuples.map((tuple) => {
					return {
						tuple: Tuple.from<unknown>(tuple.id, undefined, tuple.value!.toJson()),
						weight: 1,
					};
				})
			)
		);
	}

	let querySpecs = Map<string, RunnableQuery>();
	for (const viewSpec of Object.values(baseSpec.views)) {
		for (const subquerySpec of viewSpec.query.subquerySpecs) {
			querySpecs = querySpecs.set(
				contentId(subquerySpec),
				RunnableQuery.fromSpec(subquerySpec)
			);
		}

		const spec = viewSpec.query.spec;
		querySpecs = querySpecs.set(contentId(viewSpec.query.spec), RunnableQuery.fromSpec(spec));
	}

	return {
		currentEpoch,
		tables,
		querySpecs,

		// FIXME: Delete these??
		queryStates: Map(),
		queryResults: Map(),
		actionLog: List(),
		error: null,
	};
};

export class RebootQueryPersistence<S extends WebStore.WeakReference | ServerStore.WeakReference>
	implements QueryPersistence
{
	private _currentState: QueryEvaluatorState;

	constructor(
		private readonly _context: ContextFor<S>,
		private readonly _store: S,
		initialState: QueryEvaluatorState
	) {
		this._currentState = initialState;
	}

	localStateReplica(): QueryEvaluatorState {
		return this._currentState;
	}

	async ensurePersistedState(actionIntent: ActionIntent): Promise<void> {
		const { action } = actionIntent;
		switch (action.type) {
			case "queryRegistration":
				// TODO: enable queryRegistration on the server when it actually does something
				//await this._store.registerQuery(this._context, {
				//	queryJson: Value.fromJson(action.querySpec as JsonValue),
				//});

				this._currentState = actionIntent.proposedState;
				break;
			case "mutation": {
				const { table: tableId, mutation, intentId } = action;
				const dehydratedMutation = dehydrateTableMutation(mutation);

				// IMPORTANT: Incredibly, Google's official `Value.fromJson` implementation crashes
				// if any key in any object is `undefined`. I guess let's just turn this into a
				// string for now.
				const mutationJson = Value.fromJsonString(JSON.stringify(dehydratedMutation));

				// This stupid-looking cast is because I could not figure out how to get TypeScript
				// to not complain even though we've "proved" in the constructor that
				// `this._context` is of the correct type.
				await this._store.mutateTable(this._context as any, {
					tableId,
					mutationJson,
					intentId,
				});

				// Update the state after successful reboot operation
				this._currentState = actionIntent.proposedState;
				break;
			}
			case "columnAppend":
			case "columnDelete":
			case "columnRename":
			case "columnTypeChange":
				throw new Error("Can't change column definitions in this view");
			default: {
				const _: never = action;
				throw new Error(`Unknown action intent type: ${_}`);
			}
		}
	}
}
